// RowMenu — ⋮ dropdown na riadku zoznamu (ústava sek. 66.5)
// Použitie: <RowMenu actions={[{ label, icon, onClick, danger? }, ...]} />
//
// 🔴 OPRAVA 2026-07-28 (Mirko: „tu sa nedajú tri bodky vôbec čítať"):
// menu bolo `position:absolute; right:0` vnútri riadka. Rodičovské stĺpce zoznamov majú vlastný
// skrolovací rám (napr. Dopyty `dop-col-scroll`), takže menu sa OREZALO — z položiek bolo vidieť
// len koniec („tvoriť dopyt" namiesto „Vytvoriť dopyt").
// Teraz `position:fixed` ukotvené na rect tlačidla a ORIEZNUTÉ DO OKNA — žiadny rodič ho neoreže.
// Rovnaký princíp ako PLU picker v Predajni. PRAVIDLO: plávajúce menu nikdy nedávaj do kontajnera
// so skrolom/overflow — vždy fixed + clamp do viewportu.
import React, { useState, useRef, useEffect, useCallback } from 'react'
import { COLORS, TYPO, SPACE, RADIUS } from '../theme.js'

const OKRAJ = 8          // minimálna medzera od okraja okna
const SIRKA = 240        // šírka menu (predtým len minWidth 200)

export default function RowMenu({ actions = [], compact = false }) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState(null)
  const btnRef = useRef(null)
  const menuRef = useRef(null)

  // Spočítaj pozíciu tak, aby sa menu VŽDY zmestilo do okna.
  const zmeraj = useCallback(() => {
    const b = btnRef.current
    if (!b) return
    const r = b.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    const vyska = Math.min(actions.length * 36 + 8, vh - 2 * OKRAJ)
    // vodorovne: zarovnať pravý okraj menu s tlačidlom, ale nikdy nevyliezť z okna
    let left = r.right - SIRKA
    if (left < OKRAJ) left = OKRAJ
    if (left + SIRKA > vw - OKRAJ) left = Math.max(OKRAJ, vw - SIRKA - OKRAJ)
    // zvisle: pod tlačidlo; ak dole nie je miesto, vyklop nad
    let top = r.bottom + 4
    let maxH = vh - top - OKRAJ
    if (maxH < Math.min(vyska, 160)) {
      const hore = r.top - 4
      if (hore > maxH) { maxH = hore - OKRAJ; top = Math.max(OKRAJ, r.top - 4 - Math.min(vyska, maxH)) }
    }
    // TVRDE ORIEZNUTIE DO OKNA — poistka pre pripad, ze tlacidlo je prave mimo viditelnej
    // casti (skrolovany stlpec zoznamu): rect potom vracia suradnice mimo okna a vypocet by
    // dal nezmyselnu vysku. Menu MUSI vzdy skoncit vnutri okna.
    maxH = Math.min(maxH, vh - 2 * OKRAJ)
    if (maxH < 120) maxH = Math.min(120, vh - 2 * OKRAJ)
    if (top < OKRAJ) top = OKRAJ
    if (top + maxH > vh - OKRAJ) top = Math.max(OKRAJ, vh - OKRAJ - maxH)
    setPos({ top, left, maxH })
  }, [actions.length])

  useEffect(() => {
    if (!open) { setPos(null); return }
    zmeraj()
    function onClickOutside(e) {
      if (menuRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return
      setOpen(false)
    }
    function onEsc(e) { if (e.key === 'Escape') setOpen(false) }
    const preposun = () => zmeraj()
    document.addEventListener('mousedown', onClickOutside)
    document.addEventListener('keydown', onEsc)
    window.addEventListener('scroll', preposun, true)
    window.addEventListener('resize', preposun)
    return () => {
      document.removeEventListener('mousedown', onClickOutside)
      document.removeEventListener('keydown', onEsc)
      window.removeEventListener('scroll', preposun, true)
      window.removeEventListener('resize', preposun)
    }
  }, [open, zmeraj])

  function handleAction(action, e) {
    e.stopPropagation()
    setOpen(false)
    if (action.onClick) action.onClick(e)
  }

  return (
    <span style={{ position: 'relative', display: 'inline-block' }} onClick={e => e.stopPropagation()}>
      <button
        ref={btnRef}
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o) }}
        title="Akcie"
        style={{
          background: open ? COLORS.bgSoft : 'transparent',
          border: `1px solid ${open ? COLORS.brand : 'transparent'}`,
          borderRadius: '50%',
          width: compact ? 24 : 28,
          height: compact ? 24 : 28,
          cursor: 'pointer',
          fontSize: 16,
          color: COLORS.muted,
          padding: 0,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'inherit',
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = COLORS.bgSoft}
        onMouseLeave={(e) => { if (!open) e.currentTarget.style.background = 'transparent' }}
      >⋮</button>
      {open && pos && (
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            width: SIRKA,
            maxHeight: pos.maxH,
            overflowY: 'auto',
            background: COLORS.bg,
            border: `1px solid ${COLORS.line}`,
            borderRadius: RADIUS.md,
            boxShadow: '0 10px 28px rgba(0,0,0,0.18)',
            zIndex: 4000,
            padding: '4px 0',
          }}
        >
          {actions.map((a, i) => {
            if (a.separator) {
              return <div key={'sep' + i} style={{ height: 1, background: COLORS.line, margin: '4px 0' }} />
            }
            const disabled = a.disabled
            return (
              <button
                key={a.key || i}
                onClick={(e) => !disabled && handleAction(a, e)}
                disabled={disabled}
                title={typeof a.label === 'string' ? a.label : undefined}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: SPACE.sm,
                  width: '100%',
                  padding: '8px 14px',
                  background: 'transparent',
                  border: 'none',
                  fontSize: TYPO.sizes.sm,
                  color: disabled ? COLORS.disabled : a.danger ? COLORS.remove : COLORS.text,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                  textAlign: 'left',
                  opacity: disabled ? 0.5 : 1,
                  boxSizing: 'border-box',
                }}
                onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = a.danger ? 'rgba(239,68,68,0.08)' : COLORS.bgSoft }}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                {a.icon && <span style={{ fontSize: 14, width: 18, textAlign: 'center', flexShrink: 0 }}>{a.icon}</span>}
                <span style={{ flex: 1, minWidth: 0, whiteSpace: 'normal', lineHeight: 1.25 }}>{a.label}</span>
              </button>
            )
          })}
        </div>
      )}
    </span>
  )
}
